import React, { Component, useEffect, useState } from "react";
import { ArrowLeft, Building2, Edit, FileText, Box, AlertTriangle } from "lucide-react";
import { Supplier, PurchaseOrder, Invoice, Asset } from "../../types/supplier";
import { BasicProfileTab } from "./BasicProfileTab";
import { InvoicesTab } from "./InvoicesTab";
import { AssetsTab } from "./AssetsTab";
import { EditBasicInfoDialog } from "./EditBasicInfoDialog";
import { RMList } from "../../utils/interfaces";
import apiService from "../../services/apiService";

// Error boundary to catch any unhandled render errors and prevent blank screen
class SupplierProfileErrorBoundary extends Component<
  { children: React.ReactNode; onBack?: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; onBack?: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[SupplierProfile] Render error caught by boundary:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full min-h-[400px] flex flex-col items-center justify-center gap-4 bg-white border border-[#dde3e4] rounded-[10px] p-8 text-center font-['Poppins',sans-serif]">
          <div className="w-14 h-14 rounded-full bg-[#fef3f3] border border-[#f7c9c9] flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-[#d12d2d]" />
          </div>
          <h2 className="text-lg font-semibold text-[#151b1d]">Something went wrong</h2>
          <p className="text-sm text-[#526066] max-w-md">
            An unexpected error occurred while loading this supplier's profile.
          </p>
          <p className="text-xs text-[#7c8b91] bg-[#f6f8f8] rounded-[8px] px-3 py-2 max-w-lg break-all">
            {this.state.error?.message || "Unknown error"}
          </p>
          {this.props.onBack && (
            <button
              onClick={this.props.onBack}
              className="mt-2 h-9 px-5 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-sm font-medium transition-colors cursor-pointer"
            >
              ← Back to Suppliers
            </button>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

interface SupplierProfileProps {
  supplier: Supplier;
  onBack: () => void;
  onEdit: (supplier: Supplier) => void;
  purchaseOrders: PurchaseOrder[];
  invoices: Invoice[];
  assets: Asset[];
  indianStates: any[];
  designationList: DesignationTypes[];
  uniqueRmNames: RMList[];
  companyTypes: any[];
}

interface DesignationTypes {
  id: number;
  name: string;
  display_order: number;
}

interface PaymentSummary {
  total_amount: number;
  paid_amount: number;
  outstanding: number;
  total_invoices: number;
  total_assets: number;
}

interface ApiResponse<T> {
  data: T;
  status?: number;
}

const formatAmount = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0.00";
  try {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return "0.00";
  }
};

export function SupplierProfile({
  companyTypes,
  supplier,
  onBack,
  onEdit,
  indianStates,
  designationList,
  uniqueRmNames,
}: SupplierProfileProps) {
  const [activeTab, setActiveTab] = useState<"overview" | "invoices" | "assets">("overview");
  const [editBasicInfoOpen, setEditBasicInfoOpen] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [loadingPaymentSummary, setLoadingPaymentSummary] = useState(true);

  useEffect(() => {
    const fetchPaymentSummary = async () => {
      if (!supplier.id) {
        setLoadingPaymentSummary(false);
        return;
      }
      setLoadingPaymentSummary(true);
      try {
        const response = await apiService.get<ApiResponse<PaymentSummary>>(
          `/supplier/payment-summary/${supplier.id}`
        );
        if (response.status === 200 && response.data) {
          setPaymentSummary(response.data);
        }
      } catch {
        setPaymentSummary(null);
      } finally {
        setLoadingPaymentSummary(false);
      }
    };

    void fetchPaymentSummary();
  }, [supplier.id]);

  const companyTypeName =
    companyTypes?.find((type) => String(type.id) === String(supplier.company_type_id))?.name ||
    supplier.company_type ||
    "Private Limited";

  const primarySpoc =
    supplier.spoc_details?.find((spoc) => Number(spoc.is_primary) === 1) ||
    supplier.spoc_details?.[0];
  const primarySpocName = primarySpoc?.name || "Bhushan Mahale";

  const onboardedDate = supplier.created_at
    ? new Date(supplier.created_at).toLocaleDateString("en-GB")
    : "28/09/2026";

  const totalInvoiced = loadingPaymentSummary ? "0.00" : formatAmount(paymentSummary?.total_amount);
  const totalPaid = loadingPaymentSummary ? "0.00" : formatAmount(paymentSummary?.paid_amount);
  const totalOutstanding = loadingPaymentSummary ? "0.00" : formatAmount(paymentSummary?.outstanding);
  const invoiceCount = paymentSummary?.total_invoices ?? (supplier.total_proforma_invoices || 0);
  const invoiceCountLabel = `${invoiceCount} proforma invoice${invoiceCount === 1 ? "" : "s"}`;
  const assetCount = paymentSummary?.total_assets ?? (supplier.total_assets || 0);

  return (
    <SupplierProfileErrorBoundary onBack={onBack}>
    <div className="w-full space-y-4 text-[#151b1d] font-['Poppins',sans-serif]">
      {/* Edit Basic Info Dialog */}
      <EditBasicInfoDialog
        open={editBasicInfoOpen}
        onOpenChange={setEditBasicInfoOpen}
        supplier={supplier}
        onUpdate={onEdit}
      />

      {/* Top Breadcrumb Navigation */}
      <div className="text-xs text-[#526066] pb-1 flex items-center gap-1.5">
        <button
          onClick={onBack}
          className="hover:text-[#3f8b98] transition-colors cursor-pointer"
        >
          Suppliers
        </button>
        <span>/</span>
        <span className="font-semibold text-[#151b1d] uppercase">
          {supplier?.company_name || "Supplier"}
        </span>
      </div>

      {/* Main Supplier Header Card (Responsive & Collapsible Metadata) */}
      <div className="bg-white border border-[#dde3e4] rounded-[10px] p-5 shadow-[0_1px_2px_rgba(21,27,29,0.04),0_4px_10px_-6px_rgba(21,27,29,0.10)] w-full">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          {/* Left: Identity and Name */}
          <div className="flex items-center gap-3.5 min-w-0">
            <button
              onClick={onBack}
              className="w-9 h-9 border border-[#dde3e4] rounded-[8px] bg-white text-[#151b1d] hover:border-[#3f8b98] flex items-center justify-center flex-shrink-0 cursor-pointer shadow-sm transition-colors"
              title="Back to Suppliers"
              aria-label="Back to Suppliers"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div className="w-11 h-11 rounded-[10px] bg-[#fdf1c8] border border-[#f3dc92] text-[#d98a1c] flex items-center justify-center text-xl flex-shrink-0 shadow-sm">
              <Building2 className="w-5 h-5" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-[19px] font-semibold tracking-tight text-[#151b1d] uppercase truncate">
                  {supplier?.company_name || "Supplier"}
                </h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#e7f8ee] border border-[#bfe9cf] text-[#1e8a4c] text-[10.5px] font-semibold uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1e9a52]" />
                  Active
                </span>
              </div>
            </div>
          </div>

          {/* Right: 3 KPI Financial Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full lg:w-auto">
            {/* Total Invoiced */}
            <div className="rounded-[10px] bg-[#fdf5ef] border border-[#f6d8c3] p-3 min-w-[160px] shadow-[0_1px_2px_rgba(21,27,29,0.02)]">
              <p className="text-[10px] font-semibold tracking-wider text-[#526066] uppercase">
                TOTAL INVOICED
              </p>
              <p className="text-[18px] font-semibold text-[#b9461a] mt-0.5 tracking-tight">
                ₹{totalInvoiced}
              </p>
              <p className="text-[11px] text-[#7c8b91] mt-0.5">{invoiceCountLabel}</p>
            </div>

            {/* Paid */}
            <div className="rounded-[10px] bg-[#effaf4] border border-[#b9e8cf] p-3 min-w-[160px] shadow-[0_1px_2px_rgba(21,27,29,0.02)]">
              <p className="text-[10px] font-semibold tracking-wider text-[#526066] uppercase">
                PAID
              </p>
              <p className="text-[18px] font-semibold text-[#1c8a50] mt-0.5 tracking-tight">
                ₹{totalPaid}
              </p>
              <p className="text-[11px] text-[#7c8b91] mt-0.5">Settled against invoices</p>
            </div>

            {/* Outstanding */}
            <div className="rounded-[10px] bg-[#fef3f3] border border-[#f7c9c9] p-3 min-w-[160px] shadow-[0_1px_2px_rgba(21,27,29,0.02)]">
              <p className="text-[10px] font-semibold tracking-wider text-[#526066] uppercase">
                OUTSTANDING
              </p>
              <p className="text-[18px] font-semibold text-[#d12d2d] mt-0.5 tracking-tight">
                ₹{totalOutstanding}
              </p>
              <p className="text-[11px] text-[#7c8b91] mt-0.5">Net of TDS, incl. GST</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-4 h-px bg-[#eaeeef]" />

        {/* Bottom Row: Metadata & Edit Action */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
            <div>
              <p className="text-[10.5px] font-medium text-[#7c8b91] uppercase tracking-wider">PAN</p>
              <p className="text-[13.5px] font-semibold text-[#151b1d] mt-0.5">
                {supplier.pan || "AAHCV4369B"}
              </p>
            </div>
            <div>
              <p className="text-[10.5px] font-medium text-[#7c8b91] uppercase tracking-wider">SPOC</p>
              <p className="text-[13.5px] font-semibold text-[#151b1d] mt-0.5">
                {primarySpocName}
              </p>
            </div>
            <div>
              <p className="text-[10.5px] font-medium text-[#7c8b91] uppercase tracking-wider">COMPANY TYPE</p>
              <p className="text-[13.5px] font-semibold text-[#151b1d] mt-0.5">
                {companyTypeName}
              </p>
            </div>
            <div>
              <p className="text-[10.5px] font-medium text-[#7c8b91] uppercase tracking-wider">ONBOARDED ON</p>
              <p className="text-[13.5px] font-semibold text-[#151b1d] mt-0.5">
                {onboardedDate}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() => setEditBasicInfoOpen(true)}
              className="h-8 px-3.5 border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] rounded-[8px] text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
            >
              <Edit className="w-3.5 h-3.5 text-[#526066]" />
              <span>Edit Supplier</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Segmented Bar */}
      <div className="bg-white border border-[#dde3e4] rounded-[10px] p-1.5 grid grid-cols-3 gap-1.5 shadow-[0_1px_2px_rgba(21,27,29,0.04),0_4px_10px_-6px_rgba(21,27,29,0.10)] w-full">
        <button
          type="button"
          onClick={() => setActiveTab("overview")}
          className={`flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-[8px] text-[13px] transition-all cursor-pointer ${
            activeTab === "overview"
              ? "bg-[#347b81] text-white font-medium shadow-sm"
              : "text-[#47575e] hover:text-[#151b1d] hover:bg-[#f6f9f9] font-medium"
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Overview</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("invoices")}
          className={`flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-[8px] text-[13px] transition-all cursor-pointer ${
            activeTab === "invoices"
              ? "bg-[#347b81] text-white font-medium shadow-sm"
              : "text-[#47575e] hover:text-[#151b1d] hover:bg-[#f6f9f9] font-medium"
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Proforma Invoice</span>
          <span
            className={`text-xs px-2 py-0.5 rounded font-semibold ${
              activeTab === "invoices"
                ? "bg-white/20 text-white"
                : "bg-[#eaeff0] text-[#526066]"
            }`}
          >
            {invoiceCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("assets")}
          className={`flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-[8px] text-[13px] transition-all cursor-pointer ${
            activeTab === "assets"
              ? "bg-[#347b81] text-white font-medium shadow-sm"
              : "text-[#47575e] hover:text-[#151b1d] hover:bg-[#f6f9f9] font-medium"
          }`}
        >
          <Box className="w-4 h-4" />
          <span>Assets</span>
          <span
            className={`text-xs px-2 py-0.5 rounded font-semibold ${
              activeTab === "assets"
                ? "bg-white/20 text-white"
                : "bg-[#eaeff0] text-[#526066]"
            }`}
          >
            {assetCount}
          </span>
        </button>
      </div>

      {/* Tab Panels */}
      {activeTab === "overview" && (
        <BasicProfileTab
          supplier={supplier}
          onEdit={onEdit}
          indianStates={indianStates || []}
          designationList={designationList}
          uniqueRmNames={uniqueRmNames}
          companyTypes={companyTypes}
        />
      )}

      {activeTab === "invoices" && (
        <InvoicesTab supplierID={supplier.id} />
      )}

      {activeTab === "assets" && (
        <AssetsTab supplierID={supplier.id} />
      )}
    </div>
    </SupplierProfileErrorBoundary>
  );
}
